import Phaser from "phaser";
import * as THREE from "three";
import preloader from "../ui/preloader";
import CameraManager from "../camera/CameraManager";
import ThreeDLoader from "../utils/ThreeDLoader";
import GameObject from "../objects/GameObject";
import Awaitloader from 'phaser3-rex-plugins/plugins/awaitloader.js';
import InputController from "../controller/inputController";
import KeyboardInputProvider from "../controller/keyboardInputProvider";
import {INPUT_TYPES} from "../constants/inputs";
import GameConfig from "../config";
import MobileInputProvider from "../controller/mobileInputProvider";
import SoundManager from "../sound/SoundManager";
import {Scene, Vector3} from "three";
import SceneUtils from "../utils/sceneUtils";
import Health from "../ui/health";
import config from "../config";
import Score from "../ui/score";
import ObstacleManager from "../objects/obstacleManager";
import DecoManager from "../objects/decoManager";
import Sequence from "../ui/sequence";
import GateManager from "../objects/gateManager";
import EndGamePanel from "../ui/endgamePanel";
import LocalStorage from "../utils/localStorage";
import DbClient from "../db/client";
import postProPipeline from "../postPro/postProPipeline";
import DebugStats from "../utils/debugStats";
import BaseScene from "./baseScene";
import ThreeFX from "../fx/threeFX";
import IngameHighscore from "../ui/ingameHighscore";
import Help from "../ui/help";

export default class Game extends BaseScene
{
    constructor ()
    {
        super('Game');
    }

    async preload ()
    {
        preloader(this);
        SoundManager.preloadGame(this);
        Health.preload(this);
        Sequence.preload(this);
        Score.preload(this);
        IngameHighscore.preload(this);
        MobileInputProvider.preload(this);
        Help.preload(this);
        Awaitloader.call(this.load, async function(successCallback, failureCallback) {
            this.skyboxTexture = new THREE.TextureLoader().load( "src/assets/skybox/sky.jpg" );
            this.groundTexture = new THREE.TextureLoader().load( "src/assets/textures/grass.jpg" );
            this.walkWayTexture = new THREE.TextureLoader().load( "src/assets/textures/stone.jpg" );
            this.webTexture = new THREE.TextureLoader().load( "src/assets/textures/web1.png" );
            await ThreeFX.preload();
            this.playerGLTF = await ThreeDLoader.loadGLTF("src/assets/models/fox/fox.glb");
            this.worldGltf = await ThreeDLoader.loadGLTF("src/assets/scenes/nature/scene.gltf");
            this.ready = true; // wait for the imports
            successCallback();
        }, this)
    }

    createScene(){
        this.game.threeScene = new Scene();
        this.game.threeScene.name = "main";

        this.game.threeScene.fog = new THREE.Fog(
            GameConfig.FOG.COLOR,
            GameConfig.FOG.NEAR,
            GameConfig.FOG.FAR
        );

        this.game.gameScene = this.worldGltf.scene;
        this.game.assets = this.game.gameScene.children[0].children[0].children[0].children;
        this.game.envGroup = new THREE.Group();
        this.game.envGroup.name = "env";
        //this.game.envGroup.add(...this.game.gameScene.children);
        this.game.gameScene.traverse((c) => c.receiveShadow = true)
        this.game.threeScene.add(this.game.envGroup);

        this.game.composer = postProPipeline(this.game);

        this.game.threeScene.add()

        ThreeFX.init(this.game);
    }

    getAsset(name){
        return this.game.assets.find(a => a.name === name);
    }

    create ()
    {
        super.create();
        this.createScene()
        this.input.addPointer(3);
        this.createUI();
        this.initControls();
        this.createGround(GameConfig.GENERAL.GROUND_Y);
        this.createSkybox();
        this.createPlayer();
        this.createLight();
        CameraManager.init(this.game);
        ObstacleManager.init({
            scene: this.game.threeScene,
            blueprints: [
                this.getAsset("Stump003"),
                this.getAsset("Tree003"),
                this.getAsset("Tree003"),
            ],
        })

        if(!GameConfig.DEBUG.ON || !GameConfig.DEBUG.NO_DECO){
            DecoManager.init({
                scene: this.game.threeScene,
                blueprints: [
                    this.getAsset("Tree009"),
                    this.getAsset("Tree010"),
                    this.getAsset("Tree017"),
                    this.getAsset("Tree018")
                ]
            })
        }

        GateManager.init({
            scene: this.game.threeScene,
            blueprints: [
                this.createGateBlueprint(),
                this.getAsset("Ground003")
            ]
        })
        SoundManager.addFX("title", 0.6);
        SoundManager.addFX("jump");
        SoundManager.playMusic("title");
        this.timePassed = 0;
        this.score = 0;
        this.timeScale = 1;
        this.currentGate = null;
        this.gateSigns = config.GENERAL.GATE_SIGNS_START;
        this.gateSignIncreaseTimer = config.GENERAL.GATE_SIGN_INCREASE_TIME;
        this.scale.on("resize", () => SceneUtils.resize(this));
        this.UI = {
            health: new Health({scene: this}),
            score: new Score({scene: this}),
            sequence: new Sequence({scene: this}),
            endGamePanel: new EndGamePanel({scene: this}),
            rankPanel: new IngameHighscore({scene: this}),
            help: new Help({scene: this})
        }
        this.game.UI = this.UI;
    }

    createGateBlueprint(){
        const geo = new THREE.BoxGeometry(4, 4, 0.1);
        const material = new THREE.MeshStandardMaterial( {
            map: this.webTexture,
            transparent: true
        } );
        const mesh = new THREE.Mesh(geo, material);
        return mesh;
    }

    createGround(y){

        this.groundTexture.wrapS = THREE.RepeatWrapping;
        this.groundTexture.wrapT = THREE.RepeatWrapping;
        this.groundTexture.repeat.set( 20, 20 );
        const geo = new THREE.BoxGeometry(200, 1, 200);
        const material = new THREE.MeshStandardMaterial( {
            map: this.groundTexture
        } );
        this.ground = new THREE.Mesh(geo, material);
        this.game.threeScene.add(this.ground);
        this.ground.position.set(0, -0.5, 0);
        this.ground.receiveShadow = true;

        this.walkWayTexture.wrapS = THREE.RepeatWrapping;
        this.walkWayTexture.wrapT = THREE.RepeatWrapping;
        this.walkWayTexture.repeat.set( 1, 40 );
        const geoWalkWay = new THREE.BoxGeometry(6, 1, 200);
        const materialWalkWay = new THREE.MeshStandardMaterial( {
            map: this.walkWayTexture
        } );
        this.walkWay = new THREE.Mesh(geoWalkWay, materialWalkWay);
        this.game.threeScene.add(this.walkWay);
        this.walkWay.position.set(0, -0.49, 0);
        this.walkWay.receiveShadow = true;

    }

    initControls(){
        InputController.init();
        this.initInputs();
        if(this.game.device.input.touch){
            InputController.registerProvider(new MobileInputProvider(this));
        } else {
            InputController.registerProvider(new KeyboardInputProvider(this));
        }
    }

    hideDecoNearGates(){
        if(GameConfig.DEBUG.ON && GameConfig.DEBUG.NO_DECO) return;

        GateManager.getGates().forEach(g => {
            DecoManager.getDecoElements().forEach(d => {
                if(
                    Math.abs(d.mesh.position.x) < 8 &&
                    g.boundingBox.position.z < d.mesh.position.z + 0 &&
                    g.boundingBox.position.z > d.mesh.position.z - 12
                ) {
                    d.mesh.visible = false;
                }
            })
        })
    }

    createSkybox(){
        const skyboxGeo = new THREE.BoxGeometry(90, 90, 90);
        const material = new THREE.MeshStandardMaterial( {
            color: 0xFFFFFF,
            map: this.skyboxTexture,
            fog: false
        } );
        const skybox = new THREE.Mesh(skyboxGeo, material);
        skybox.name = "skybox";
        skybox.material.side = THREE.BackSide;

        this.game.threeScene.add(skybox);

        const geo = new THREE.BoxGeometry( 100, 15, 1);
        const mat = new THREE.MeshStandardMaterial( {color: 0xffffff} );

        const fogBlocker = new THREE.Mesh( geo, mat );

        this.game.threeScene.add(fogBlocker);
        fogBlocker.position.set(0,0,40);
    }

    createPlayer(){
        const mesh = this.playerGLTF.scene.children[0];
        const mixer = new THREE.AnimationMixer(this.playerGLTF.scene);
        this.playerGLTF.animations.forEach(animation => {
            mixer.clipAction(animation)
        })
        mesh.mixer = mixer;
        const position = new THREE.Vector3(0,1,0);
        this.player = new GameObject({
            mesh,
            game: this,
            parent: this.game.threeScene,
            position
        })
        console.log("player created");
    }

    createLight(){
        const light = new THREE.AmbientLight(
            config.LIGHT.AMBIENT.COLOR,
            config.LIGHT.AMBIENT.INTENSITY
        ); // soft white light
        light.name = "ambientLight"
        this.game.threeScene.add( light );
        const dLight = new THREE.DirectionalLight(
            config.LIGHT.DIRECTIONAL.COLOR,
            config.LIGHT.DIRECTIONAL.INTENSITY
        );
        dLight.name = "DirectionalLight"
        dLight.castShadow = true;
        this.game.threeScene.add(dLight);
        dLight.position.set(
            config.LIGHT.DIRECTIONAL.POSITION.X,
            config.LIGHT.DIRECTIONAL.POSITION.Y,
            config.LIGHT.DIRECTIONAL.POSITION.Z
        );
        dLight.shadow.mapSize.width = 512; // default
        dLight.shadow.mapSize.height = 512; // default
        dLight.shadow.camera.near = 0.5; // default
        dLight.shadow.camera.far = 500; // default
        dLight.lookAt(new THREE.Vector3(
            config.LIGHT.DIRECTIONAL.TARGET_POSITION.X,
            config.LIGHT.DIRECTIONAL.TARGET_POSITION.Y,
            config.LIGHT.DIRECTIONAL.TARGET_POSITION.Z
        ));
    }

    createUI(){
        console.log("create ui");
    }

    increaseSequence(){
        this.sequenceStep++;
        this.UI.sequence.arrowHit();
        if(!this.neededSequence[this.sequenceStep]){
            if(this.currentGate) {
                this.currentGate.done=true;
                ThreeFX.emitSparks(this.currentGate.mesh.position.clone().add(new Vector3(0,2.5,0)))
                this.tweens.add({
                    targets: [this.currentGate.meshWeb.scale],
                    x: 0, y: 0, z: 0,
                    duration: 80,
                    delay: 80,
                    ease: Phaser.Math.Easing.Back.In
                })
            }
            this.fightingEnemy = false;
            this.player.attack();
            this.UI.sequence.destroy();
        }
    }

    breakCombo(){
        this.sequenceStep = 0;
        this.UI.sequence.reset();
    }

    initInputs(){
        InputController.eventTarget.addEventListener("keyup", (ev) => {
            if(!this.fightingEnemy) return;
            if(ev.detail === INPUT_TYPES.LEFT){
                if(this.neededSequence[this.sequenceStep] === INPUT_TYPES.LEFT) {
                    this.increaseSequence();
                } else {
                    this.breakCombo();
                }
            }
            if(ev.detail === INPUT_TYPES.RIGHT){
                if(this.neededSequence[this.sequenceStep] === INPUT_TYPES.RIGHT){
                    this.increaseSequence();
                } else {
                    this.breakCombo();
                }
            }
            if(ev.detail === INPUT_TYPES.UP){
                if(this.neededSequence[this.sequenceStep] === INPUT_TYPES.UP){
                    this.increaseSequence();
                } else {
                    this.breakCombo();
                }
            }
            if(ev.detail === INPUT_TYPES.DOWN){
                if(this.neededSequence[this.sequenceStep] === INPUT_TYPES.DOWN){
                    this.increaseSequence();
                } else {
                    this.breakCombo();
                }
            }

        })
    }

    handleInputs(){
        const state = InputController.getState();

        if(this.fightingEnemy || this.player.dead) return;

        if(state[INPUT_TYPES.LEFT]){
            this.player.turnLeft();
        }

        if(state[INPUT_TYPES.RIGHT]){
            this.player.turnRight();
        }

        if(state[INPUT_TYPES.JUMP]){
            this.player.jump();
        }
    }

    updateDebug(){
        this.game.stats.end();
        this.game.stats.begin();
        DebugStats.updateDrawCalls(this.game.composer.renderer.info.render.calls);
        DebugStats.updatePolys(this.game.composer.renderer.info.render.triangles)
        DebugStats.updateGeos(this.game.composer.renderer.info.memory.geometries)
        DebugStats.updateTextures(this.game.composer.renderer.info.memory.textures)
        ThreeFX.renderDebug();
    }

    update(elapsedTotal, elapsedMS){
        if(!this.ready || this.stopped) return;
        if(GameConfig.DEBUG.ON){
            this.updateDebug();
        }

        this.handleInputs();
        const deltaS = elapsedMS / 1000;
        const effectiveDeltaS = this.slowMotion ? deltaS * this.timeScale : deltaS;

        CameraManager.update(this.player.mesh, effectiveDeltaS);
        ThreeFX.proton.update(effectiveDeltaS);
        //this.game.threeRenderer.render(this.game.threeScene, this.game.threeCamera);
        this.player.update(effectiveDeltaS);
        this.updateObstacles(effectiveDeltaS);
        this.updateDeco(effectiveDeltaS);
        this.updateGates(effectiveDeltaS);
        this.updateGround(effectiveDeltaS);
        this.hideDecoNearGates();
        if(!this.player.dead) {
            this.checkCollision();
            this.player.speed += effectiveDeltaS * config.GENERAL.SPEED_ACCELERATION;
            if(this.player.speed >= config.GENERAL.SPEED_CAP) {
                this.player.speed = config.GENERAL.SPEED_CAP;
            }
            this.timePassed += effectiveDeltaS;
            this.gateSignIncreaseTimer -= effectiveDeltaS;
            if(this.gateSignIncreaseTimer <= 0){
                this.gateSignIncreaseTimer = config.GENERAL.GATE_SIGN_INCREASE_TIME + this.gateSignIncreaseTimer;
                if(this.gateSigns < config.GENERAL.GATE_SIGN_MAX) {
                    this.gateSigns++;
                }
            }
            this.score = Math.floor(this.timePassed);
            this.UI.score.changeScore(this.score);
            this.UI.rankPanel.update(this.score);
            this.UI.help.update(this.timePassed);
        }
        this.game.composer.render();
    }

    updateGround(deltaS){
        this.ground.material.map.offset.y -= (deltaS * this.player.speed) / 10;
        this.walkWay.material.map.offset.y -= (deltaS * this.player.speed) / 5;
    }

    updateGates(deltaS){
        GateManager.moveZ(-deltaS * this.player.speed)
    }

    updateObstacles(deltaS){
        ObstacleManager.moveZ(-deltaS * this.player.speed);
    }

    updateDeco(deltaS){
        if(GameConfig.DEBUG.ON && GameConfig.DEBUG.NO_DECO) return;
        DecoManager.moveZ(-deltaS * this.player.speed)
    }

    handleCollision(){
        const lifeLoss = this.player.handleCollision();
        if(lifeLoss) {
            this.UI.health.removeLife();
            CameraManager.shakeRequest(0.3, 20);
            this.fightingEnemy = false;
        }
        if(this.player.life <= 0) this.gameOver();
    }

    checkCollision() {
        const boxPlayer = new THREE.Box3()
        boxPlayer.copy( this.player.boundingBox.geometry.boundingBox ).applyMatrix4( this.player.boundingBox.matrixWorld );

        const intersection = ObstacleManager.getObstacles().find(o => {
            o.boundingBox.updateMatrixWorld(true);
            const boxObstacle = new THREE.Box3();
            boxObstacle.copy( o.boundingBox.geometry.boundingBox ).applyMatrix4( o.boundingBox.matrixWorld );
            return boxObstacle.intersectsBox(boxPlayer)
        });
        if(intersection) {
            this.handleCollision();
        }

        const gateIntersection = GateManager.getGates().find(sg => {
            if(sg.done) return;
            sg.boundingBox.updateMatrixWorld(true);
            const boxObstacle = new THREE.Box3();
            boxObstacle.copy( sg.boundingBox.geometry.boundingBox ).applyMatrix4( sg.boundingBox.matrixWorld );
            return boxObstacle.intersectsBox(boxPlayer)
        })

        if(gateIntersection && !this.slowMotion) {
            this.currentGate = gateIntersection;
            this.slowMotion = true;
            this.tweens.add({
                targets: [this, this.tweens],
                timeScale: config.GENERAL.SLOW_MOTION_TIME_SCALE,
                duration: 200,
                ease: Phaser.Math.Easing.Sine.In
            }).on("complete", () => {
                this.createEnemy()
            })
            //this.tweens.timeScale = config.GENERAL.SLOW_MOTION_TIME_SCALE;
        } else if(!gateIntersection && this.slowMotion){
            this.tweens.add({
                targets: [this, this.tweens],
                timeScale: 1,
                duration: 300,
                ease: Phaser.Math.Easing.Sine.Out
            }).on("complete", () => {
                this.slowMotion = false;
                if(this.fightingEnemy) {
                    this.handleCollision();
                    this.UI.sequence.destroy();
                    this.tweens.add({
                        targets: [this.currentGate.meshWeb.scale],
                        x: 0, y: 0, z: 0,
                        duration: 50,
                        ease: Phaser.Math.Easing.Back.In
                    })
                }
            })
            //this.tweens.timeScale = 1;
        }
    }

    createRandomSequence(){
        const keys = [
            INPUT_TYPES.LEFT,
            INPUT_TYPES.RIGHT,
            INPUT_TYPES.UP,
            INPUT_TYPES.DOWN
        ]
        const sequence = [];
        for(let i = 0; i < this.gateSigns; i++){
            const random = Math.floor(Math.random() * 4);
            const key = keys[random];
            sequence.push(key);
        }
        return sequence;
    }

    createEnemy(){
        this.neededSequence = this.createRandomSequence();
        this.UI.sequence.addSequence(this.neededSequence);
        this.sequenceStep = 0;
        this.fightingEnemy = true;
    }

    gameOver(){
        //this.stopped = true;
        this.player.die().then(() => {
            LocalStorage.addHighscoreEntry(this.score, LocalStorage.getPlayerName());
            DbClient.addHighscore({score: this.score, name: LocalStorage.getPlayerName(), nameHash: LocalStorage.getNameHash()});
            this.UI.endGamePanel.changeScore(this.score);
            this.UI.endGamePanel.show();
        });
    }
}